import { v4 as uuidV4, v5 as uuidV5 } from 'uuid';
import { LatLng } from 'leaflet';
import { parseAbsolute, parseZonedDateTime } from '@internationalized/date';
import {
  AccommodationLeg,
  AirportLegLocationReference,
  AviaLegTypes,
  CommercialFlightAviaLeg,
  CommercialLegType,
  GroundLegFacilityLocationReference,
  GroundLegTypes,
  GroundLocationReference,
  LegLocationReferenceTypeEnum,
  LegLocationTypeEnum,
  LogisticsLegResourceType,
  RouteLegType,
  RouteLegTypeEnum,
  SegmentTypeEnum,
  TmdxAffiliateGroundLeg,
  TmdxJetAviaLeg,
  GroundVehicleType,
  CustomLocationType,
  TmdxDriveTedsGroundLeg,
  TmdxDriveAffiliateGroundLeg,
  RouteLegResponseType,
  ResponseRouteLegTypeEnum,
  LegLocationType,
  GroundLegCustomLocationReference,
} from '@/api/apis/route';
import { CaseFacility, CaseResourceType } from '@/api/apis/case';
import {
  ActionTypeEnum,
  PlanActionTeamKeyword,
  TaskAction,
  TaskLocation,
  TeamResourcesByKeyword,
} from '@/api/apis/mission-planner';
import { queryClient } from '@/queries';
import { airportsQuery } from '@/queries/airports';
import { getDefaultFbo } from '@/queries/route-planner/utils';
import { homeLocationsQueryKeys } from '@/queries/homelocation/homelocation.keys';
import { REPOSITION_SEGMENT_TYPES } from '@/utils/route/config';
import { hubQueries } from '@/queries/hub';
import { Hub } from '@/api/apis/facilities';
import { calculateGroundTimeDuration } from '@/utils/route/calculations';
import { CalculateGroundTimeType } from '@/api/apis/here-maps';
import { hereMapsQueries } from '@/queries/here-maps';
import { MINUTE_IN_MS } from '@/constants/date.constants';

const TASK_LOCATION_TYPE_MAP: Record<string, LegLocationTypeEnum> = {
  Hub: LegLocationTypeEnum.hub,
  Hospital: LegLocationTypeEnum.hospital,
  Airport: LegLocationTypeEnum.airport,
  TxCenter: LegLocationTypeEnum.txcenter,
  HomeLocation: LegLocationTypeEnum.homeResidence,
  BasicLocation: LegLocationTypeEnum.custom,
  SurgeonBasicLocation: LegLocationTypeEnum.custom,
  PointLocation: LegLocationTypeEnum.custom,
};

export const getFixedTaskLocationType = (taskLocation: TaskLocation): Partial<LegLocationType> => {
  const isBasicLocationType = ['SurgeonBasicLocation', 'BasicLocation'].includes(taskLocation.abstractLocationType);
  const locationType =
    isBasicLocationType && taskLocation.originalObjectType
      ? TASK_LOCATION_TYPE_MAP[taskLocation.originalObjectType]
      : TASK_LOCATION_TYPE_MAP[taskLocation.abstractLocationType];

  return {
    id: taskLocation.originalObjectId || taskLocation.id,
    type: locationType,
  };
};

const getResourcesByTeamKeyword = (
  teamResources: Partial<TeamResourcesByKeyword>,
  teamKeyword?: PlanActionTeamKeyword,
) => {
  if (!teamKeyword || !teamResources[teamKeyword]) return [];

  switch (teamKeyword) {
    case PlanActionTeamKeyword.surgeonOnly:
      return [
        {
          resourceId: teamResources[teamKeyword].surgeon.id,
          resourceType: CaseResourceType.Surgeon,
        },
      ];
    case PlanActionTeamKeyword.teamWithoutOcs:
      return [
        {
          resourceId: teamResources[teamKeyword].specialist.id,
          resourceType: CaseResourceType.ClinicalSpecialist,
        },
      ];
    case PlanActionTeamKeyword.team:
      return [
        {
          resourceId: teamResources[teamKeyword].specialist.id,
          resourceType: CaseResourceType.ClinicalSpecialist,
        },
        {
          resourceId: teamResources[teamKeyword].ocs.id,
          resourceType: CaseResourceType.OCSConsole,
        },
      ];
    default:
      return [];
  }
};

// Convert TaskAction to RouteLeg
const getActionResources = (
  action: TaskAction,
  teamResources: Partial<TeamResourcesByKeyword>,
): LogisticsLegResourceType[] => {
  const mainResources = getResourcesByTeamKeyword(teamResources, action.teamKeyword);

  const crewTeam =
    action.crew?.map((crew) => ({
      resourceId: crew.id,
      resourceType: CaseResourceType.Crew,
    })) || [];

  return [...mainResources, ...crewTeam];
};

export const getActionAirportLocation = async (
  location: TaskLocation,
): Promise<AirportLegLocationReference & { timezone: string }> => {
  const locationItem = await queryClient.ensureQueryData(airportsQuery.item(location.id));
  const locationFbo = getDefaultFbo(locationItem?.fbos);

  return {
    $type: LegLocationReferenceTypeEnum.airport,
    id: location.id,
    airportId: location.id,
    locationId: location.id,
    locationType: LegLocationTypeEnum.airport,
    timezone: locationItem.location.timezone,
    ...(locationFbo?.id
      ? {
          fboReference: {
            fboLocationId: locationFbo.id,
            fboAddressId: locationFbo.addresses[0]?.id || 0,
          },
        }
      : {}),
  };
};

const getFacilityFromTask = (
  data: Pick<TaskLocation, 'id'>,
): Omit<GroundLegFacilityLocationReference, 'locationType' | 'locationReference'> & {
  locationReference: Omit<GroundLegFacilityLocationReference['locationReference'], 'locationType'>;
} => ({
  $type: LegLocationReferenceTypeEnum.facility,
  id: data.id,
  locationId: data.id,
  airportReference: null,
  locationReference: {
    $type: LegLocationReferenceTypeEnum.facility,
    id: data.id,
    locationId: data.id,
  },
});

export const getActionFacilityLocation = (
  data: Pick<TaskLocation, 'id' | 'originalObjectId' | 'location'> & Partial<LegLocationType>,
): GroundLegFacilityLocationReference & { timezone: string } => {
  const location = getFacilityFromTask(data);

  switch (data.type) {
    case LegLocationTypeEnum.homeResidence:
      const homeLocation = queryClient.getQueryData(
        homeLocationsQueryKeys.getItem(data.originalObjectId),
      ) as CustomLocationType;

      return {
        ...location,
        locationType: LegLocationTypeEnum.homeResidence,
        locationReference: {
          ...location.locationReference,
          locationType: LegLocationTypeEnum.homeResidence,
        },
        timezone: homeLocation?.timezone || 'America/New_York',
      };
    case LegLocationTypeEnum.hub:
      return {
        ...location,
        locationType: LegLocationTypeEnum.hub,
        locationReference: {
          ...location.locationReference,
          locationType: LegLocationTypeEnum.hub,
        },
        timezone: data.location.timezone,
      };
    case LegLocationTypeEnum.txcenter:
      return {
        ...location,
        locationType: LegLocationTypeEnum.txcenter,
        locationReference: {
          ...location.locationReference,
          locationType: LegLocationTypeEnum.txcenter,
        },
        timezone: data.location.timezone,
      };
    case LegLocationTypeEnum.opo:
      return {
        ...location,
        locationType: LegLocationTypeEnum.opo,
        locationReference: {
          ...location.locationReference,
          locationType: LegLocationTypeEnum.opo,
        },
        timezone: data.location.timezone,
      };
    case LegLocationTypeEnum.hospital:
    default:
      return {
        ...location,
        locationType: LegLocationTypeEnum.hospital,
        locationReference: {
          ...location.locationReference,
          locationType: LegLocationTypeEnum.hospital,
        },
        timezone: data.location.timezone,
      };
  }
};

// HERE ids are not uuids, so custom locations get an id derived from the HERE id. Being deterministic keeps
// the separately converted main, surgeon and commercial plans pointing at the same location.
export const getCustomLocationId = (hereMapsId: string): string => uuidV5(`here-maps:${hereMapsId}`, uuidV5.URL);

const getCustomLocationType = async (
  data: Pick<TaskLocation, 'id' | 'originalObjectId' | 'location'> & Partial<LegLocationType>,
): Promise<(GroundLegCustomLocationReference & { timezone: string }) | null> => {
  const addressData = await queryClient.ensureQueryData(
    hereMapsQueries.getAddressByReverseGeocode([data.location.latitude, data.location.longitude]),
  );

  if (!addressData) return null;

  const locationId = getCustomLocationId(addressData.id);

  return {
    $type: LegLocationReferenceTypeEnum.custom,
    id: locationId,
    locationId,
    locationType: LegLocationTypeEnum.custom,
    airportReference: null,
    locationReference: {
      $type: LegLocationReferenceTypeEnum.custom,
      id: locationId,
      locationId,
      locationType: LegLocationTypeEnum.custom,
      hereMapsId: addressData.id,
    },
    timezone: addressData.timeZone.name,
  };
};

const getGroundActionLocation = async (data: TaskLocation): Promise<GroundLocationReference & { timezone: string }> => {
  const fixedData = getFixedTaskLocationType(data);

  const location = {
    ...data,
    ...fixedData,
  };

  switch (location.type) {
    case LegLocationTypeEnum.airport:
      const { timezone, ...airportLocation } = await getActionAirportLocation(location);
      return {
        $type: LegLocationReferenceTypeEnum.airport,
        id: airportLocation.locationId,
        locationId: airportLocation.locationId,
        locationType: LegLocationTypeEnum.airport,
        airportReference: airportLocation,
        locationReference: null,
        timezone,
      };
    case LegLocationTypeEnum.custom:
      const address = await getCustomLocationType(location);

      if (!address) return getActionFacilityLocation(location);
      return address;
    case LegLocationTypeEnum.hub:
    case LegLocationTypeEnum.homeResidence:
    case LegLocationTypeEnum.txcenter:
    case LegLocationTypeEnum.hospital:
    case LegLocationTypeEnum.opo:
    default:
      return getActionFacilityLocation(location);
  }
};

const convertFlyActionToRouteLeg = async (
  action: TaskAction,
  currentSegmentType: SegmentTypeEnum,
  legs: (AviaLegTypes | GroundLegTypes)[],
  teamResources: Partial<TeamResourcesByKeyword>,
): Promise<TmdxJetAviaLeg> => {
  const currentAircraft = {
    resourceId: `${action.aircraftInfo!.id}`,
    resourceType: CaseResourceType.Aircraft,
  };

  const resources = [...getActionResources(action, teamResources), currentAircraft];
  const { timezone: departureLocalTimezone, ...departureLocation } = await getActionAirportLocation(action.from);
  const { timezone: arrivalLocalTimezone, ...arrivalLocation } = await getActionAirportLocation(action.to);
  const prevLeg = legs.length ? legs[legs.length - 1] : null;

  return {
    id: action.id,
    legType: RouteLegTypeEnum.TmdxJetAviaLeg,
    segmentType: currentSegmentType,
    comments: '',
    departureLocation,
    arrivalLocation,
    departureTime: action.startTime,
    departureLocalTimezone,
    arrivalTime: action.endTime,
    arrivalLocalTimezone,
    chainPosition: {
      previousLegId: prevLeg?.id || null,
      nextLegId: null,
      groupId: prevLeg?.chainPosition.groupId || uuidV4(),
    },
    hasScs: false,
    resources: REPOSITION_SEGMENT_TYPES.includes(currentSegmentType)
      ? resources.filter((r) => [CaseResourceType.Aircraft, CaseResourceType.Crew].includes(r.resourceType))
      : resources,
  };
};

const convertCommercialFlightActionToRouteLeg = async (
  action: TaskAction,
  legs: (AviaLegTypes | GroundLegTypes)[],
  teamResources: Partial<TeamResourcesByKeyword>,
): Promise<CommercialFlightAviaLeg> => {
  const {
    timezone: departureLocalTimezone,
    fboReference: _,
    ...departureLocation
  } = await getActionAirportLocation(action.from);
  const {
    timezone: arrivalLocalTimezone,
    fboReference: __,
    ...arrivalLocation
  } = await getActionAirportLocation(action.to);
  const prevLeg = legs.length ? legs[legs.length - 1] : null;

  return {
    id: action.id,
    legType: RouteLegTypeEnum.CommercialFlightAviaLeg,
    segmentType: SegmentTypeEnum.custom,
    comments: '',
    departureLocation,
    arrivalLocation,
    departureTime: action.startTime,
    departureLocalTimezone,
    arrivalTime: action.endTime,
    arrivalLocalTimezone,
    chainPosition: {
      previousLegId: prevLeg?.id || null,
      nextLegId: null,
      groupId: prevLeg?.chainPosition.groupId || uuidV4(),
    },
    hasScs: false,
    resources: getActionResources(action, teamResources),
    bookingNumber: '',
    flightNumber: action.commercialAircraft?.registration || '',
    priceInCents: 0,
    commercialLegType: CommercialLegType.Manual,
  };
};

const convertStayOvernightActionToRouteLeg = (
  action: TaskAction,
  teamResources: Partial<TeamResourcesByKeyword>,
): AccommodationLeg => {
  const resources = getActionResources(action, teamResources);

  return {
    id: action.id,
    legType: RouteLegTypeEnum.AccommodationLeg,
    segmentType: SegmentTypeEnum.custom,
    location: {
      $type: LegLocationReferenceTypeEnum.custom,
      id: '',
      locationId: '',
      locationType: LegLocationTypeEnum.custom,
      hereMapsId: '',
    },
    checkInAt: action.startTime,
    checkOutAt: action.endTime,
    resources,
    hotelName: '',
    rooms: resources.length - 1 || 1,
    bookingNumber: '',
    hasScs: false,
    comments: '',
  };
};

const convertDriveActionToTmdxDriveRouteLeg = async (
  action: TaskAction,
  currentSegmentType: SegmentTypeEnum,
  legs: (AviaLegTypes | GroundLegTypes)[],
  teamResources: Partial<TeamResourcesByKeyword>,
): Promise<TmdxDriveAffiliateGroundLeg> => {
  const { timezone: departureLocalTimezone, ...departureLocation } = await getGroundActionLocation(action.from);
  const { timezone: arrivalLocalTimezone, ...arrivalLocation } = await getGroundActionLocation(action.to);
  const prevLeg = legs.length ? legs[legs.length - 1] : null;

  return {
    id: action.id,
    legType: RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
    segmentType: currentSegmentType,
    comments: '',
    departureLocation,
    arrivalLocation,
    departureTime: action.startTime,
    departureLocalTimezone,
    arrivalTime: action.endTime,
    arrivalLocalTimezone,
    chainPosition: {
      previousLegId: prevLeg?.id || null,
      nextLegId: null,
      groupId: prevLeg?.chainPosition.groupId || uuidV4(),
    },
    hasScs: false,
    resources: getActionResources(action, teamResources),
    distanceInMeters: 0,
    isFeasibleForDrive: true,
  };
};

const convertDriveActionToTmdxAffiliateRouteLeg = async (
  action: TaskAction,
  currentSegmentType: SegmentTypeEnum,
  legs: (AviaLegTypes | GroundLegTypes)[],
  teamResources: Partial<TeamResourcesByKeyword>,
): Promise<TmdxAffiliateGroundLeg> => {
  const { timezone: departureLocalTimezone, ...departureLocation } = await getGroundActionLocation(action.from);
  const { timezone: arrivalLocalTimezone, ...arrivalLocation } = await getGroundActionLocation(action.to);
  const prevLeg = legs.length ? legs[legs.length - 1] : null;

  return {
    id: action.id,
    legType: RouteLegTypeEnum.TmdxAffiliateGroundLeg,
    segmentType: currentSegmentType,
    comments: '',
    departureLocation,
    arrivalLocation,
    departureTime: action.startTime,
    departureLocalTimezone,
    arrivalTime: action.endTime,
    arrivalLocalTimezone,
    chainPosition: {
      previousLegId: prevLeg?.id || null,
      nextLegId: null,
      groupId: prevLeg?.chainPosition.groupId || uuidV4(),
    },
    hasScs: false,
    resources: getActionResources(action, teamResources),
    distanceInMeters: 0,
    isFeasibleForDrive: true,
    driver: '',
    email: '',
    phoneNumber: '',
    plateNumber: '',
    vehicleType: GroundVehicleType.SUV,
    reservationNumber: '',
    pickup: '',
    dropoff: '',
    wait: '',
    stay: '',
  };
};

type GenerateTmdxDriveTedsRouteLegsParams = {
  txCenter: CaseFacility;
  hubId: string;
  departureTime: string;
  deviceResource: LogisticsLegResourceType;
};

export const generateTmdxDriveTedsRouteLegs = async ({
  txCenter,
  hubId,
  departureTime,
  deviceResource,
}: GenerateTmdxDriveTedsRouteLegsParams): Promise<TmdxDriveTedsGroundLeg> => {
  const hubItem = queryClient.getQueryData(hubQueries.item(hubId).queryKey) as Hub;
  const { timezone: departureLocalTimezone, ...departureLocation } = getActionFacilityLocation({
    id: txCenter.id,
    originalObjectId: txCenter.id,
    type: LegLocationTypeEnum.txcenter,
    location: txCenter.location,
  });
  const { timezone: arrivalLocalTimezone, ...arrivalLocation } = getActionFacilityLocation({
    id: hubItem.id,
    originalObjectId: hubItem.id,
    type: LegLocationTypeEnum.hub,
    location: hubItem.location,
  });

  const timeDurationData = await calculateGroundTimeDuration(
    new LatLng(txCenter.location.latitude, txCenter.location.longitude),
    new LatLng(hubItem.location.latitude, hubItem.location.longitude),
    CalculateGroundTimeType.DepartureTime,
    departureTime,
  );

  const departureTimeUtc = parseZonedDateTime(departureTime).toAbsoluteString();

  const arrivalTime = parseAbsolute(departureTimeUtc, 'UTC')
    .add({ milliseconds: timeDurationData.timeDurationInMs > 0 ? timeDurationData.timeDurationInMs : MINUTE_IN_MS })
    .toAbsoluteString();

  return {
    id: uuidV4(),
    legType: RouteLegTypeEnum.TmdxDriveTedsGroundLeg,
    segmentType: SegmentTypeEnum.hub,
    comments: '',
    departureLocation,
    arrivalLocation,
    departureTime: departureTimeUtc,
    departureLocalTimezone,
    arrivalTime,
    arrivalLocalTimezone,
    chainPosition: {
      previousLegId: null,
      nextLegId: null,
      groupId: uuidV4(),
    },
    hasScs: false,
    resources: [deviceResource],
    distanceInMeters: timeDurationData.distanceInMeters || 0,
    isFeasibleForDrive: timeDurationData.timeDurationInMs !== -1,
  };
};

export type ConvertTaskActionToRouteLegParams = {
  action: TaskAction;
  currentSegment: SegmentTypeEnum;
  legs: (AviaLegTypes | GroundLegTypes)[];
  teamResources: Partial<TeamResourcesByKeyword>;
  isTmdxDriveCompatible?: boolean;
};

export const convertTaskActionToRouteLeg = async ({
  action,
  currentSegment,
  legs,
  teamResources,
  isTmdxDriveCompatible = false,
}: ConvertTaskActionToRouteLegParams): Promise<RouteLegType> => {
  switch (action.actionType) {
    case ActionTypeEnum.FlyAction:
      return convertFlyActionToRouteLeg(action, currentSegment, legs, teamResources);
    case ActionTypeEnum.CommercialFlightAction:
      return convertCommercialFlightActionToRouteLeg(action, legs, teamResources);
    case ActionTypeEnum.StayOvernightAction:
      return convertStayOvernightActionToRouteLeg(action, teamResources);
    case ActionTypeEnum.DriveAction:
    default:
      return isTmdxDriveCompatible
        ? convertDriveActionToTmdxDriveRouteLeg(action, currentSegment, legs, teamResources)
        : convertDriveActionToTmdxAffiliateRouteLeg(action, currentSegment, legs, teamResources);
  }
};

export const checkTxCenterProvidersInTransit = (
  legs: (RouteLegResponseType | AviaLegTypes | GroundLegTypes)[],
  type: 'raw' | 'transformed' = 'transformed',
) => {
  const dhToTxSegmentLegs = legs.filter((leg) => leg.segmentType === SegmentTypeEnum.txcenter);

  if (type === 'raw') {
    const responseList = dhToTxSegmentLegs as RouteLegResponseType[];

    const hasNonRawTxCenterLegs = responseList.some(
      (leg) =>
        ![
          ResponseRouteLegTypeEnum.TxCenterProviderGroundLeg,
          ResponseRouteLegTypeEnum.TxCenterProviderAviaLeg,
        ].includes(leg.$type),
    );

    return !hasNonRawTxCenterLegs;
  }

  const list = dhToTxSegmentLegs as (AviaLegTypes | GroundLegTypes)[];

  const hasNonTxCenterLegs = list.some(
    (leg) =>
      ![RouteLegTypeEnum.TxCenterProviderGroundLeg, RouteLegTypeEnum.TxCenterProviderAviaLeg].includes(leg.legType),
  );

  return !hasNonTxCenterLegs;
};
